import React, { useEffect, useState } from "react";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";

const ACTION_COLORS: Record<string, string> = {
  "family.delete":     "badge-root",
  "kid.delete":        "badge-root",
  "completion.delete": "badge-root",
  "backup.restore":    "badge-root",
  "member.revoke":     "badge-root",
  "kid.pin_reset":     "badge-admin",
  "kid.balance_adjust":"badge-admin",
  "family.edit":       "badge-admin",
  "backup.export":     "badge-l2",
  "photo.view":        "badge-l2",
};

export default function AuditLog() {
  const { hasPermission } = useAuthStore();
  const [entries,      setEntries]      = useState<any[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { const res = await adminApi.listAudit({ limit: 200 }); setEntries(res.entries); }
    finally { setLoading(false); }
  }

  async function exportCsv() {
    const res = await adminApi.exportAudit();
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }

  if (!hasPermission("metrics:read")) {
    return <div className="card" style={{ color: "var(--text-muted)" }}>metrics:read permission required.</div>;
  }

  const filtered = filterAction ? entries.filter((e) => e.action === filterAction) : entries;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 900, flex: 1 }}>📋 Audit Log</h1>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 12 }}>
          <option value="">All actions</option>
          {[...new Set(entries.map((e) => e.action))].sort().map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button className="btn" onClick={exportCsv}>⬇ Export CSV</button>
        <button className="btn" onClick={load} disabled={loading}>Refresh</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--surface2)", color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em" }}>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Time</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Action</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Actor</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Family</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <React.Fragment key={e.auditId}>
                <tr style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                  onClick={() => setExpanded(expanded === e.auditId ? null : e.auditId)}>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", color: "var(--text-muted)" }}>{new Date(e.createdAt).toLocaleString()}</td>
                  <td style={{ padding: "9px 14px" }}>
                    <span className={`badge ${ACTION_COLORS[e.action] ?? "badge-l2"}`}>{e.action}</span>
                    {e.assumedRole && <span className="badge badge-admin" style={{ marginLeft: 4, fontSize: 9 }}>👁 {e.assumedRole}</span>}
                  </td>
                  <td style={{ padding: "9px 14px", color: "var(--text-muted)" }}>{e.actorRole} · {e.actorId?.slice(0,8)}</td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)" }}>{e.targetFamilyId ?? "—"}</td>
                </tr>
                {expanded === e.auditId && (
                  <tr style={{ background: "var(--surface2)" }}>
                    <td colSpan={4} style={{ padding: "10px 14px" }}>
                      <pre style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)", whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>No audit entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
